import { TIER_COLORS, ALL_DAYS } from "../utils/tiers";
import { computeStats } from "../utils/stats";

function fmtHours(h) {
  if (!h) return "0";
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
}

export default function StatsView({ players, groups, coaches }) {
  const s = computeStats(players, groups, coaches);

  const maxDayHours = Math.max(1, ...s.byDay.map(d => d.hours));
  const maxTierHours = Math.max(1, ...s.byTier.map(t => t.hours));
  // Coaches sorted busiest-first, so the weekly load ranking reads top-down.
  const coachesByHours = [...s.coachStats].sort((a, b) => b.hours - a.hours);
  const maxCoachHours = Math.max(1, s.unassignedGroupHours, ...coachesByHours.map(c => c.hours));

  return (
    <div className="p-4 space-y-8">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          label="Total weekly hours"
          value={`${fmtHours(s.totalHours)}h`}
          sub={`${s.scheduledCount} scheduled group${s.scheduledCount !== 1 ? "s" : ""}`}
          accent="text-blue-700"
        />
        <Kpi
          label="Coaches needed (peak)"
          value={s.peakConcurrency}
          sub={s.peakDay ? `${s.peakDay} at ${s.peakTime}` : "No sessions scheduled"}
          accent="text-purple-700"
        />
        <Kpi
          label="Coaches created"
          value={s.coachCount}
          sub={`${fmtHours(s.assignedCoachHours)}h assigned`}
          accent="text-teal-700"
        />
        <Kpi
          label="Fill rate"
          value={`${Math.round(s.fillRate * 100)}%`}
          sub={`${s.filledSpots}/${s.totalSpots} spots · ${s.unassignedPlayers} unplaced`}
          accent="text-green-700"
        />
      </div>

      {/* Coaches-needed explainer */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 text-sm text-purple-900">
        <span className="font-semibold">How many pros do you need?</span> At minimum{" "}
        <span className="font-semibold">{s.peakConcurrency}</span>{" "}— that's the most groups running at the
        same moment ({s.peakDay ? `${s.peakDay} ${s.peakTime}` : "—"}), and one pro can't teach two groups at
        once. Across the whole week there are <span className="font-semibold">{fmtHours(s.totalHours)}</span>{" "}
        instruction hours to cover. If each pro works up to <em>H</em> hours/week, you'll want at least{" "}
        <span className="font-semibold">ceil({fmtHours(s.totalHours)} ÷ H)</span> pros — whichever is larger.
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hours by day */}
        <Panel title="Hours by day">
          <div className="space-y-2">
            {s.byDay.map(d => (
              <div key={d.day} className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-20 flex-shrink-0">{d.day}</span>
                <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden">
                  <div
                    className="h-full bg-blue-400 rounded"
                    style={{ width: `${(d.hours / maxDayHours) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-gray-700 w-14 text-right flex-shrink-0">
                  {fmtHours(d.hours)}h
                </span>
              </div>
            ))}
          </div>
        </Panel>

        {/* Hours by tier */}
        <Panel title="Hours by roster tier">
          {s.byTier.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No scheduled groups yet.</p>
          ) : (
            <div className="space-y-2">
              {s.byTier.map(t => {
                const c = TIER_COLORS[t.tier] || TIER_COLORS["No Group"];
                return (
                  <div key={t.tier} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-40 flex-shrink-0 truncate" title={t.tier}>{t.tier}</span>
                    <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden">
                      <div
                        className={`h-full ${c.header} rounded`}
                        style={{ width: `${(t.hours / maxTierHours) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-700 w-20 text-right flex-shrink-0">
                      {fmtHours(t.hours)}h · {t.groups}g
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      {/* Weekly hours by coach */}
      <Panel title="Weekly hours by coach">
        {coachesByHours.length === 0 ? (
          <p className="text-sm text-gray-400 italic">
            No coaches yet. Add one below, then assign them to groups to see their weekly hours here.
          </p>
        ) : (
          <div className="space-y-2">
            {coachesByHours.map(cs => (
              <div key={cs.coach.id} className="flex items-center gap-3">
                <span className="text-xs text-gray-600 w-36 flex-shrink-0 truncate" title={cs.coach.name}>{cs.coach.name}</span>
                <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden">
                  <div
                    className={`h-full rounded ${cs.overCapacity ? "bg-red-400" : "bg-teal-400"}`}
                    style={{ width: `${(cs.hours / maxCoachHours) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-gray-700 w-28 text-right flex-shrink-0">
                  {fmtHours(cs.hours)}h
                  {cs.coach.maxHours != null && <span className="text-gray-400"> / {fmtHours(cs.coach.maxHours)}h</span>}
                  <span className="text-gray-400"> · {cs.groupCount}g</span>
                </span>
              </div>
            ))}
            {s.unassignedGroupHours > 0 && (
              <div className="flex items-center gap-3 pt-1 border-t border-gray-100">
                <span className="text-xs text-amber-600 w-36 flex-shrink-0 truncate italic">Unassigned groups</span>
                <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden">
                  <div
                    className="h-full rounded bg-amber-300"
                    style={{ width: `${(s.unassignedGroupHours / maxCoachHours) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-amber-600 w-28 text-right flex-shrink-0">
                  {fmtHours(s.unassignedGroupHours)}h · {s.unassignedGroupCount}g
                </span>
              </div>
            )}
          </div>
        )}
      </Panel>

      {/* Hours per coach per day */}
      {coachesByHours.length > 0 && (
        <Panel title="Hours by coach, per day">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-max">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className="text-left font-semibold py-1.5 pr-4">Coach</th>
                  {ALL_DAYS.map(day => (
                    <th key={day} className="text-right font-semibold py-1.5 px-3 w-16">{day.slice(0, 3)}</th>
                  ))}
                  <th className="text-right font-semibold py-1.5 pl-3 w-16">Total</th>
                </tr>
              </thead>
              <tbody>
                {coachesByHours.map(cs => (
                  <tr key={cs.coach.id} className="border-t border-gray-100">
                    <td className="py-1.5 pr-4 text-gray-700 truncate max-w-[12rem]" title={cs.coach.name}>{cs.coach.name}</td>
                    {ALL_DAYS.map(day => {
                      const h = cs.hoursByDay[day];
                      return (
                        <td key={day} className={`text-right py-1.5 px-3 ${h ? "text-gray-700" : "text-gray-300"}`}>
                          {h ? `${fmtHours(h)}h` : "–"}
                        </td>
                      );
                    })}
                    <td className={`text-right py-1.5 pl-3 font-semibold ${cs.overCapacity ? "text-red-600" : "text-gray-800"}`}>
                      {fmtHours(cs.hours)}h
                    </td>
                  </tr>
                ))}
                {/* Column totals across all coaches */}
                <tr className="border-t-2 border-gray-200 text-xs text-gray-500 font-semibold">
                  <td className="py-1.5 pr-4">All coaches</td>
                  {ALL_DAYS.map(day => {
                    const total = coachesByHours.reduce((sum, cs) => sum + cs.hoursByDay[day], 0);
                    return (
                      <td key={day} className="text-right py-1.5 px-3">{total ? `${fmtHours(total)}h` : "–"}</td>
                    );
                  })}
                  <td className="text-right py-1.5 pl-3">{fmtHours(s.assignedCoachHours)}h</td>
                </tr>
              </tbody>
            </table>
          </div>
          {s.unassignedGroupHours > 0 && (
            <p className="text-xs text-amber-600 mt-2">
              Plus {fmtHours(s.unassignedGroupHours)}h across {s.unassignedGroupCount} group{s.unassignedGroupCount !== 1 ? "s" : ""} with no coach assigned.
            </p>
          )}
        </Panel>
      )}

      {/* Quick facts */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MiniStat label="Players" value={s.totalPlayers} />
        <MiniStat label="Assigned" value={s.assignedPlayers} />
        <MiniStat label="Unplaced" value={s.unassignedPlayers} warn={s.unassignedPlayers > 0} />
        <MiniStat label="Scheduled groups" value={s.scheduledCount} />
        <MiniStat label="Unscheduled" value={s.unscheduledCount} warn={s.unscheduledCount > 0} />
        {s.byCourtType.map(ct => (
          <MiniStat key={ct.type} label={`${ct.type} hrs`} value={`${fmtHours(ct.hours)}h`} />
        ))}
      </div>

      {/* Coaches are created & managed on the Coaches tab now; Stats only
          reports on their workload (the charts above). */}
      {s.coachCount === 0 && (
        <p className="text-sm text-gray-400 italic">
          No coaches yet. Head to the <span className="font-medium text-gray-500">Coaches</span> tab to add pros and assign them to groups.
        </p>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</div>
      <div className={`text-3xl font-bold mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, warn }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
      <div className="text-xs text-gray-400">{label}</div>
      <div className={`text-lg font-bold ${warn ? "text-amber-600" : "text-gray-800"}`}>{value}</div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{title}</h3>
      {children}
    </div>
  );
}
